import { createSelector } from 'reselect';
import { getSelectedCountryKey, getSelectedPlatformKey } from '@/selectors/data';
import { getManualCountryKeys, getSupportedCountryKeys } from '@/selectors/options';
import {
  getSupportedByCountryPlatformKeys,
  getUnsupportedPlatformKeys,
  getNoLinkPlatformKeys,
  getNativePlatformKeys,
  getStandardPlatformKeys,
  getManualContactPlatformKeys,
  getPlatforms,
} from '@/selectors/platforms';
import { OTHER_VALUE } from '@/containers/Platform/constants';
import { getCompatibleProductsFlattened } from './products';

const hasKey = (key, keys = []) => keys.includes(key);

/***************/
/*   COUNTRY   */
/***************/
export const hasSupportedCountry = createSelector(
  getSelectedCountryKey,
  getSupportedCountryKeys,
  hasKey,
);

export const hasManualRedirectCountry = createSelector(
  getSelectedCountryKey,
  getManualCountryKeys,
  (country, manualCountries) => hasKey(country, manualCountries),
);

export const hasCountry = key =>
  createSelector(getSelectedCountryKey, selectedCountryKey => key === selectedCountryKey);

/***************/
/*  PLATFORM   */
/***************/
export const hasStandardPlatform = createSelector(
  getSelectedPlatformKey,
  getStandardPlatformKeys,
  hasKey,
);

export const hasSupportedPlatform = createSelector(
  getSelectedPlatformKey,
  getSupportedByCountryPlatformKeys,
  hasKey,
);

export const hasUnsupportedPlatform = createSelector(
  getSelectedPlatformKey,
  getUnsupportedPlatformKeys,
  hasKey,
);

export const hasNoPlatforms = createSelector(
  getPlatforms,
  getSelectedCountryKey,
  (platforms, countryKey) => {
    const platformsInCountry = platforms.filter(({ supportedCountries = [] }) =>
      supportedCountries.includes(countryKey),
    );

    return platformsInCountry.length === 0;
  },
);

export const hasNativePlatform = createSelector(
  getSelectedPlatformKey,
  getNativePlatformKeys,
  hasKey,
);
export const isCountrySweden = hasCountry('SE');
export const isCountryUS = hasCountry('US');

export const hasNoLinkPlatform = createSelector(
  getSelectedPlatformKey,
  getNoLinkPlatformKeys,
  hasKey,
);

export const hasOtherPlatform = createSelector(
  getSelectedPlatformKey,
  platform => platform === OTHER_VALUE,
);

export const hasManualContactPlatform = createSelector(
  getSelectedPlatformKey,
  getManualContactPlatformKeys,
  hasKey,
);

/***************/
/*    MIXES    */
/***************/
export const hasSupportedCountryAndPlatform = createSelector(
  hasSupportedCountry,
  hasSupportedPlatform,
  (country, platform) => country && platform,
);

/************************/
/*  PRODUCTS temporal   */
/************************/
export const hasSupportedProductsForKustom = createSelector(
  getCompatibleProductsFlattened,
  products => {
    const kustomProducts = products.filter(p => p.id.startsWith('kco') && p.isCompatible);

    return kustomProducts?.length > 0 || false;
  },
);
